import React, { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import {
  fetchAdminRankings, fetchPlayers, refreshData, fetchLeagues, fetchLeague,
  getAdminToken, getUser, setUser as persistUser,
} from "@/lib/api";
import { TradeCalculator } from "@/components/TradeCalculator";
import { TradeFinder } from "@/components/TradeFinder";
import { TierListMaker } from "@/components/TierListMaker";
import { RankingsTable } from "@/components/RankingsTable";
import { PicksPanel } from "@/components/PicksPanel";
import { LoginGate } from "@/components/LoginGate";
import { LeaguesPanel } from "@/components/LeaguesPanel";
import { LeagueTrades } from "@/components/LeagueTrades";
import { Brand } from "@/components/Brand";
import { AdminRankingsPage } from "@/components/AdminRankingsPage";
import { PublicTrystomaticTrack } from "@/components/PublicTrystomaticTrack";
import { SleeperLookupPage } from "@/components/SleeperLookupPage";
import { NewsPanel } from "@/components/NewsPanel";
import { MobileToolNav } from "@/components/MobileToolNav";
import { AdSlot } from "@/components/AdSlot";
import { PrivacyPage } from "@/components/PrivacyPage";
import { AboutPage } from "@/components/AboutPage";
import { SupportPage } from "@/components/SupportPage";
import { applyAdminBlendToLeague, applyAdminBlendToPlayers } from "@/lib/adminRankings";
import { fetchFantasyCalcValues, mergePlayersWithFantasyCalc } from "@/lib/fantasyCalcMerge";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { getAdSenseSlot, isAdsEnabled } from "@/lib/ads";
import { RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";

const LEAGUE_KEY = "trystomatic.league";
const OPEN_TOOL_KEY = "trystomatic.openTool";

function guestUser() {
  return { user_id: "guest", username: "guest", display_name: "Guest", is_guest: true };
}

function parseAppPathname(pathname) {
  const isPublicTrackRoute = pathname === "/track/trystomatic-leagues";
  const isAdminRankingsRoute = pathname === "/admin/rankings";
  const isLookupRoute = pathname === "/lookup" || pathname.startsWith("/lookup/");
  const isPrivacyRoute = pathname === "/privacy" || pathname === "/privacy/";
  const isAboutRoute = pathname === "/about" || pathname === "/about/";
  const isSupportRoute = pathname === "/support" || pathname === "/support/";
  return { isPublicTrackRoute, isAdminRankingsRoute, isLookupRoute, isPrivacyRoute, isAboutRoute, isSupportRoute };
}

function useAppPathname() {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);
  return pathname;
}

function playerLoadErrorMessage(err) {
  if (err?.response?.status === 401) return null;
  const msg = apiErrorMessage(err);
  if (msg === "Something went wrong. Try again.") return "Failed to load player data.";
  return `Failed to load player data: ${msg}`;
}

function Header({ user, onLogout, onSignIn, leagues, selectedLeagueId, onSelectLeague, format, setFormat, onRefresh, refreshing, fetchedAt, activeTool, onToolChange, showAdminRefresh }) {
  return (
    <header className="border-b border-[#0A0A0A] bg-white sticky top-0 z-40 pt-[env(safe-area-inset-top)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-3 md:py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 md:gap-4 flex-wrap min-w-0">
          <Brand size="md" />
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-[#52525B] font-mono-data border-l border-[#0A0A0A]/20 pl-4">
            Dynasty Trade Calculator
          </span>
          <div className="hidden md:inline-flex border border-[#0A0A0A] bg-white" data-testid="top-tool-tabs">
            <button
              onClick={() => onToolChange("trade")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${activeTool === "trade" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              data-testid="tab-trade-calculator"
            >
              Trade Calculator
            </button>
            <button
              onClick={() => onToolChange("league")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors border-l border-[#0A0A0A] ${activeTool === "league" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              data-testid="tab-your-league"
            >
              Your League
            </button>
            <button
              onClick={() => onToolChange("news")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors border-l border-[#0A0A0A] ${activeTool === "news" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              data-testid="tab-news"
            >
              News
            </button>
            <button
              onClick={() => onToolChange("lookup")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors border-l border-[#0A0A0A] ${activeTool === "lookup" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              data-testid="nav-sleeper-lookup"
            >
              Profile Lookup
            </button>
            <button
              onClick={() => onToolChange("tiers")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors border-l border-[#0A0A0A] ${activeTool === "tiers" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              data-testid="tab-tier-list"
            >
              Tier List Maker
            </button>
            <button
              onClick={() => onToolChange("track")}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors border-l border-[#0A0A0A] ${activeTool === "track" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              data-testid="tab-track-trystomatic"
            >
              Track Trystomatic
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {user.is_guest ? (
            <button
              type="button"
              onClick={onSignIn}
              className="px-3 py-2 text-xs uppercase tracking-widest border border-[#0A0A0A] bg-white hover:bg-[#F3F4F6] font-mono-data max-w-[220px] text-left"
              data-testid="league-sign-in"
            >
              Leagues · Sign in
            </button>
          ) : leagues.length > 0 ? (
            <select
              value={selectedLeagueId || ""}
              onChange={(e) => onSelectLeague(e.target.value || null)}
              className="px-3 py-2 text-xs uppercase tracking-widest border border-[#0A0A0A] bg-white hover:bg-[#F3F4F6] font-mono-data max-w-[220px]"
              data-testid="league-selector"
            >
              <option value="">No league</option>
              {leagues.map((lg) => (
                <option key={lg.league_id} value={lg.league_id}>{lg.name}</option>
              ))}
            </select>
          ) : null}
          {(activeTool === "trade" || activeTool === "tiers") && (
          <div className="inline-flex border border-[#0A0A0A]" data-testid="format-toggle">
            <button onClick={() => setFormat("superflex")} className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${format === "superflex" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`} data-testid="format-superflex">Superflex</button>
            <button onClick={() => setFormat("onqb")} className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors border-l border-[#0A0A0A] ${format === "onqb" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F3F4F6]"}`} data-testid="format-onqb">1QB</button>
          </div>
          )}
          {showAdminRefresh && (
            <button onClick={onRefresh} disabled={refreshing} className="brutal-btn-outline px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50" data-testid="refresh-btn">
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh data
            </button>
          )}
          <div className="hidden md:flex items-center gap-2 border-l border-[#0A0A0A]/20 pl-2 ml-1">
            {user.is_guest ? (
              <>
                <span className="text-xs font-mono-data text-[#52525B]">Guest</span>
                <button onClick={onLogout} className="text-xs uppercase tracking-widest text-[#7C3AED] hover:text-[#0A0A0A] transition-colors font-semibold" data-testid="sign-in-btn">Sign in</button>
              </>
            ) : (
              <>
                <span className="text-xs font-mono-data">@{user.username}</span>
                <button onClick={onLogout} className="text-[#52525B] hover:text-[#FF3B30] transition-colors" data-testid="logout-btn" title="Sign out"><LogOut size={14} /></button>
              </>
            )}
          </div>
        </div>
      </div>
      {fetchedAt && (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-2 text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-mono-data">
          Data updated · {new Date(fetchedAt).toLocaleString()}
        </div>
      )}
    </header>
  );
}

function Hero({ format, user, leagueDetail }) {
  const myTeam = leagueDetail?.rosters?.find(r => r.is_me);
  return (
    <section className="border-b border-[#0A0A0A] bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 lg:py-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <span className="label-eyebrow">{user.is_guest ? "Guest mode" : `Signed in · @${user.username}`}</span>
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter leading-none mt-1">
            Settle the trade. <span className="text-[#52525B]">Trust the <span className="text-[#7C3AED]">value</span>.</span>
          </h1>
          <p className="mt-2 text-xs text-[#52525B] max-w-2xl leading-relaxed">
            One TRYSTOMATIC value per asset. Plug into your Sleeper league and weigh deals against real rosters — no second-guessing, no five tabs open.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 min-w-[260px]">
          <div className="brutal-card px-3 py-2">
            <div className="label-eyebrow">Format</div>
            <div className="font-display font-black text-lg tracking-tight">{format === "superflex" ? "Superflex" : "1QB"}</div>
          </div>
          <div className="brutal-card px-3 py-2">
            <div className="label-eyebrow">Your Team</div>
            <div className="font-display font-black text-lg tracking-tight truncate">{myTeam ? myTeam.team_name : (user.is_guest ? "—" : "Pick a league")}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const adsOn = isAdsEnabled();
  const footerSlot = getAdSenseSlot("FOOTER");
  const showFooterAd = adsOn && Boolean(footerSlot);
  return (
    <footer className="border-t border-[#0A0A0A] mt-20">
      {showFooterAd ? (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-6 pb-2">
          <AdSlot slot={footerSlot} minHeight={90} testId="ad-slot-footer" />
        </div>
      ) : null}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 text-xs text-[#52525B] flex items-center justify-between flex-wrap gap-2 font-mono-data">
        <span>TRYSTOMATIC · Dynasty trade calculator · {new Date().getFullYear()}</span>
        <span className="flex items-center gap-3 flex-wrap justify-end">
          {adsOn ? <span className="text-[#A1A1AA]">Ads help keep Trystomatic free</span> : null}
          <a href="/about" className="text-[#52525B] hover:text-[#7C3AED] transition-colors" data-testid="footer-about">
            About
          </a>
          <a href="/privacy" className="text-[#52525B] hover:text-[#7C3AED] transition-colors" data-testid="footer-privacy">
            Privacy
          </a>
          <a href="/support" className="text-[#52525B] hover:text-[#7C3AED] transition-colors" data-testid="footer-support">
            Support
          </a>
          <span>Built for dynasty league degens.</span>
        </span>
      </div>
    </footer>
  );
}

export default function App() {
  const pathname = useAppPathname();
  const { isPublicTrackRoute, isAdminRankingsRoute, isLookupRoute, isPrivacyRoute, isAboutRoute, isSupportRoute } = parseAppPathname(pathname);
  const isStaticInfoRoute = isPrivacyRoute || isAboutRoute || isSupportRoute;
  const [user, setLoggedUser] = useState(() => {
    const u = getUser();
    if (u) return u;
    if (parseAppPathname(window.location.pathname).isLookupRoute) {
      const g = guestUser();
      persistUser(g);
      return g;
    }
    return null;
  });
  const [format, setFormat] = useState("superflex");
  const [data, setData] = useState({ players: [], fp_count: 0, fetched_at: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState(() => localStorage.getItem(LEAGUE_KEY) || null);
  const [leagueDetail, setLeagueDetail] = useState(null);
  const [activeTool, setActiveTool] = useState(() => {
    if (parseAppPathname(window.location.pathname).isLookupRoute) return "lookup";
    try {
      const pending = sessionStorage.getItem(OPEN_TOOL_KEY);
      if (pending) {
        sessionStorage.removeItem(OPEN_TOOL_KEY);
        return pending;
      }
    } catch {
      /* ignore */
    }
    return "trade";
  });
  const tradeRef = useRef(null);

  const handleToolChange = useCallback((tool) => {
    setActiveTool(tool);
    if (tool === "lookup") {
      if (!window.location.pathname.startsWith("/lookup")) {
        window.history.pushState({}, "", "/lookup");
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    } else if (window.location.pathname.startsWith("/lookup")) {
      window.history.pushState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, []);

  useEffect(() => {
    if (isLookupRoute && activeTool !== "lookup") {
      setActiveTool("lookup");
    } else if (!isLookupRoute && activeTool === "lookup") {
      setActiveTool("trade");
    }
  }, [activeTool, isLookupRoute]);

  const handleLogin = useCallback((u) => {
    setLoggedUser(u);
    try {
      const pending = sessionStorage.getItem(OPEN_TOOL_KEY);
      if (pending) {
        sessionStorage.removeItem(OPEN_TOOL_KEY);
        setActiveTool(pending);
        if (pending === "lookup") {
          window.history.pushState({}, "", "/lookup");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    if (!user || isAdminRankingsRoute || isPublicTrackRoute || isStaticInfoRoute || activeTool === "lookup" || activeTool === "news") return;
    setLoading(true);
    try {
      const [d, adminRankings] = await Promise.all([
        fetchPlayers(format),
        fetchAdminRankings(format).catch(() => null),
      ]);
      let fantasyCalc = { byKey: new Map(), byName: new Map(), count: 0 };
      try {
        fantasyCalc = await fetchFantasyCalcValues();
      } catch {
        /* same as admin: fall back to backend fcValue when FC is blocked */
      }
      const merged = mergePlayersWithFantasyCalc(d.players || [], fantasyCalc);
      const blendedPlayers = applyAdminBlendToPlayers(merged, {
        order: adminRankings?.order?.length ? adminRankings.order : undefined,
        blendWeight: adminRankings?.blendWeight,
      });
      const nextData = { ...d, players: blendedPlayers };
      setData(nextData);
      return nextData;
    } catch (e) {
      if (e?.response?.status === 401) {
        persistUser(null);
        setLoggedUser(null);
        toast.error("Session expired. Sign in with Sleeper again.");
      } else {
        const msg = playerLoadErrorMessage(e);
        if (msg) toast.error(msg);
      }
    } finally { setLoading(false); }
  }, [format, activeTool, isAdminRankingsRoute, isPublicTrackRoute, isStaticInfoRoute, user]);

  useEffect(() => { load(); }, [load]);

  // Sleeper user: load leagues then auto-select stored or first
  useEffect(() => {
    if (isPublicTrackRoute || isStaticInfoRoute || activeTool === "lookup") return;
    if (!user || user.is_guest) {
      setLeagues([]);
      setLeagueDetail(null);
      if (user?.is_guest) {
        localStorage.removeItem(LEAGUE_KEY);
        setSelectedLeagueId(null);
      }
      return;
    }
    (async () => {
      try {
        const d = await fetchLeagues();
        setLeagues(d.leagues || []);
        if (d.leagues?.length) {
          const stored = localStorage.getItem(LEAGUE_KEY);
          const valid = d.leagues.find(l => l.league_id === stored);
          setSelectedLeagueId(valid ? stored : d.leagues[0].league_id);
        } else {
          localStorage.removeItem(LEAGUE_KEY);
          setSelectedLeagueId(null);
          setLeagueDetail(null);
        }
      } catch (e) { /* silent */ }
    })();
  }, [activeTool, isAdminRankingsRoute, isPublicTrackRoute, isStaticInfoRoute, user]);

  // Fetch league detail when selection changes
  useEffect(() => {
    if (isAdminRankingsRoute || isPublicTrackRoute || isStaticInfoRoute || activeTool === "lookup") return;
    if (!selectedLeagueId) { setLeagueDetail(null); return; }
    localStorage.setItem(LEAGUE_KEY, selectedLeagueId);
    (async () => {
      try {
        const d = await fetchLeague(selectedLeagueId);
        setLeagueDetail(applyAdminBlendToLeague(d, data.players));
        // Sync format to league format
        if (d.format && d.format !== format) setFormat(d.format);
      } catch (e) {
        toast.error("Failed to load league");
        setLeagueDetail(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeagueId]);

  useEffect(() => {
    if (isAdminRankingsRoute || isPublicTrackRoute || isStaticInfoRoute || activeTool === "lookup") return;
    setLeagueDetail((current) => applyAdminBlendToLeague(current, data.players));
  }, [data.players]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadAppData = useCallback(async (leagueId = selectedLeagueId, { pullFantasyCalc = false } = {}) => {
    setRefreshing(true);
    try {
      if (pullFantasyCalc) {
        if (!getAdminToken()) {
          toast.error("Sign in at /admin/rankings first to pull new FantasyCalc data.");
          return;
        }
        await refreshData();
      }
      const nextData = await load();
      const playersForValues = nextData?.players || data.players;
      if (leagueId) {
        const d = await fetchLeague(leagueId);
        setLeagueDetail(applyAdminBlendToLeague(d, playersForValues));
        if (d.format && d.format !== format) setFormat(d.format);
      } else {
        setLeagueDetail(null);
      }
      toast.success(pullFantasyCalc ? "FantasyCalc data refreshed" : "Reloaded");
    } catch (e) {
      if (e?.response?.status === 401 && pullFantasyCalc) {
        toast.error("Admin session expired — sign in again at /admin/rankings.");
      } else {
        toast.error("Reload failed");
      }
    }
    finally { setRefreshing(false); }
  }, [data.players, format, load, selectedLeagueId]);

  const handleRefresh = async () => {
    await reloadAppData(selectedLeagueId, { pullFantasyCalc: true });
  };

  const handleSelectLeague = (leagueId) => {
    setSelectedLeagueId(leagueId);
  };

  const handleSignIn = () => { persistUser(null); setLoggedUser(null); localStorage.removeItem(LEAGUE_KEY); };

  const handleLogout = () => { handleSignIn(); };

  const jumpToTradeCalculator = useCallback((fn) => {
    handleToolChange("trade");
    requestAnimationFrame(() => {
      fn();
      document.getElementById("trade-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [handleToolChange]);

  const handleAddToSide = (side, player) => {
    jumpToTradeCalculator(() => {
      tradeRef.current?.addToSide(side, player);
      toast.success(`Added ${player.name} to Side ${side}`);
    });
  };

  const handleLoadTeam = (side, roster) => {
    jumpToTradeCalculator(() => {
      tradeRef.current?.loadTeam(side, roster);
    });
  };

  const handleJumpToTradeCalculator = (targetRoster, targetPlayer, outgoingAssets = []) => {
    const myRoster = leagueDetail?.rosters?.find((r) => r.is_me);
    jumpToTradeCalculator(() => {
      tradeRef.current?.startTradeFromFinder(myRoster, targetRoster, targetPlayer, outgoingAssets);
      toast.success(`Loaded ${targetPlayer.name} into the trade calculator`);
    });
  };

  if (isPrivacyRoute) return (
    <>
      <Toaster position="top-right" richColors />
      <PrivacyPage />
    </>
  );

  if (isAboutRoute) return (
    <>
      <Toaster position="top-right" richColors />
      <AboutPage />
    </>
  );

  if (isSupportRoute) return (
    <>
      <Toaster position="top-right" richColors />
      <SupportPage />
    </>
  );

  if (isPublicTrackRoute) return (
    <>
      <Toaster position="top-right" richColors />
      <PublicTrystomaticTrack />
    </>
  );

  if (isAdminRankingsRoute) return <AdminRankingsPage />;

  if (!user) return (<><Toaster position="top-right" richColors /><LoginGate onLogin={handleLogin} /></>);

  return (
    <div className="App min-h-screen pb-[calc(52px+env(safe-area-inset-bottom))] md:pb-0">
      <Toaster position="top-right" richColors />
      <Header
        user={user} onLogout={handleLogout} onSignIn={handleSignIn}
        leagues={leagues}
        selectedLeagueId={selectedLeagueId}
        onSelectLeague={handleSelectLeague}
        format={format} setFormat={setFormat}
        onRefresh={handleRefresh} refreshing={refreshing}
        fetchedAt={activeTool === "trade" || activeTool === "tiers" ? data.fetched_at : null}
        activeTool={activeTool}
        onToolChange={handleToolChange}
        showAdminRefresh={Boolean(getAdminToken())}
      />

      {activeTool !== "track" && activeTool !== "lookup" && activeTool !== "league" && activeTool !== "news" && <Hero format={format} user={user} leagueDetail={leagueDetail} />}

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {activeTool === "track" ? (
          <PublicTrystomaticTrack embedded />
        ) : activeTool === "lookup" ? (
          <SleeperLookupPage embedded />
        ) : activeTool === "news" ? (
          <NewsPanel
            embedded
            isGuest={user.is_guest}
            onSignIn={handleSignIn}
            user={user}
            selectedLeagueId={selectedLeagueId}
            leagues={leagues}
          />
        ) : loading && data.players.length === 0 && activeTool !== "league" ? (
          <div className="py-32 text-center text-[#52525B] text-sm font-mono-data">Loading rankings…</div>
        ) : (
          <>
            <div className={activeTool === "trade" ? "block" : "hidden"} data-testid="trade-tool-panel">
            <div id="trade-section">
              <TradeCalculator
                players={data.players}
                ref={tradeRef}
                leagueDetail={leagueDetail}
              />
            </div>
            <TradeFinder
              isGuest={user.is_guest}
              onSignIn={handleSignIn}
              leagueDetail={leagueDetail}
              onJumpToTradeCalculator={handleJumpToTradeCalculator}
            />
            <RankingsTable players={data.players} fpCount={data.fp_count} />
            <PicksPanel players={data.players} />
            </div>
            <div className={activeTool === "tiers" ? "block" : "hidden"} data-testid="tier-tool-panel">
              <TierListMaker players={data.players} />
            </div>
            <div className={activeTool === "league" ? "block" : "hidden"} data-testid="league-tool-panel">
              <LeaguesPanel
                embedded
                isGuest={user.is_guest}
                onSignIn={handleSignIn}
                user={user}
                leagues={leagues}
                selectedLeagueId={selectedLeagueId}
                onSelectLeague={handleSelectLeague}
                leagueDetail={leagueDetail}
                onLoadTeam={handleLoadTeam}
                onAddToSide={handleAddToSide}
              />
              <LeagueTrades
                embedded
                isGuest={user.is_guest}
                onSignIn={handleSignIn}
                leagueId={selectedLeagueId}
                leagueName={leagueDetail?.name}
                players={data.players}
              />
            </div>
          </>
        )}
      </main>

      <MobileToolNav activeTool={activeTool} onToolChange={handleToolChange} />
      <Footer />
    </div>
  );
}
